/**
 * M1-26 — devices (§7.4, §7.21), and M1-28's backup state alongside it.
 *
 * The acceptance is *an owner can recover from a stolen PC without contacting
 * us*, and the hard part is the confirmation. §7.4 asks it to state what
 * release does **and does not** do to local data, and the "does not" is the
 * half that matters here: releasing a device does not reach the stolen machine
 * and does not erase anything on it. An owner who believes otherwise will not
 * take the other steps they should.
 *
 * Backup state sits on this page rather than its own, because "has my data got
 * off that machine?" is the same question as "can I safely stop using it?" and
 * an owner asks them in one breath.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'

import { api, type BackupDevice, type BackupState, type Device, type DevicesView } from '../api.js'
import { date, dateTime, relativeHours } from '../format.js'
import { Alert, Card, Confirm, Pill } from '../components/ui.js'
import { messageFor } from './Register.js'
import { SetupCode } from './SetupCode.js'

/** One phrase per backup health, so the tone matches the actual urgency. */
function backupLine(backup: BackupDevice): { tone: 'ok' | 'warn' | 'error' | 'muted'; text: string } {
  switch (backup.health) {
    case 'never':
      return {
        tone: 'warn',
        text: 'Nothing backed up yet. The till backs up automatically once it is online.',
      }
    case 'current':
      return { tone: 'ok', text: `Backed up ${relativeHours(backup.lastSyncAgeHours)}.` }
    case 'stale':
      return { tone: 'warn', text: `Last backed up ${relativeHours(backup.lastSyncAgeHours)}.` }
    case 'overdue':
      return {
        tone: 'error',
        text: `Not backed up for ${relativeHours(backup.lastSyncAgeHours)}. Check the shop's internet.`,
      }
    case 'released':
      return { tone: 'muted', text: `Last backed up ${dateTime(backup.lastSyncAt)}.` }
  }
}

function BackupDetail({ backup }: { backup: BackupDevice }) {
  const line = backupLine(backup)

  return (
    <div className="devices__backup">
      <Pill tone={line.tone}>{line.text}</Pill>
      <span className="devices__meta">
        {backup.records.toLocaleString('en-KE')} records held
        {backup.reportedQueueDepth !== null && backup.reportedQueueDepth > 0 ? (
          <>
            {' · '}
            {/* Stated as the till's own claim, not as something we measured —
                the server cannot know what it has never been sent. */}
            the till last reported {backup.reportedQueueDepth.toLocaleString('en-KE')} waiting to
            send
            {backup.reportedOldestUnsyncedAgeHours !== null
              ? `, oldest ${relativeHours(backup.reportedOldestUnsyncedAgeHours)}`
              : ''}
          </>
        ) : null}
      </span>
    </div>
  )
}

export function Devices() {
  const [view, setView] = useState<DevicesView | null>(null)
  const [backup, setBackup] = useState<BackupState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<Device | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [devices, state] = await Promise.all([
      api.devices(),
      // Backup state is supporting detail; the page is still useful without
      // it, so a failure here must not blank the device list.
      api.backup().catch(() => null),
    ])
    setView(devices)
    setBackup(state)
  }, [])

  useEffect(() => {
    void load().catch((err: unknown) => setError(messageFor(err)))
  }, [load])

  async function release(deviceId: string) {
    setBusy(true)
    setError(null)
    try {
      await api.releaseDevice(deviceId)
      setConfirming(null)
      await load()
    } catch (err) {
      setError(messageFor(err))
      setConfirming(null)
    } finally {
      setBusy(false)
    }
  }

  if (error && !view) return <Alert>{error}</Alert>
  if (!view) return <p className="empty">Loading…</p>

  const backupFor = (deviceId: string) => backup?.devices.find((d) => d.deviceId === deviceId)

  return (
    <>
      {error ? <Alert>{error}</Alert> : null}
      {backup?.attentionNeeded ? (
        <Alert tone="warn">
          One of your devices has not backed up recently. Its sales are safe on the machine
          itself, but they are not in this account yet.
        </Alert>
      ) : null}

      <SetupCode canEnrol={view.allowance.canEnrolAnother} />

      <Card
        title="Devices"
        note={
          view.allowance.canEnrolAnother
            ? 'Run the installer on the shop PC, then enter a setup code at first run.'
            : undefined
        }
        action={
          <Pill tone={view.allowance.canEnrolAnother ? 'muted' : 'ok'}>
            {view.allowance.used} of {view.allowance.limit}
          </Pill>
        }
      >
        {view.devices.length === 0 ? (
          <p className="empty">
            No device set up yet. <Link to="/download">Download the installer</Link>, run it on
            the shop PC, and enter a setup code at first run.
          </p>
        ) : (
          <ul className="devices">
            {view.devices.map((device) => {
              const state = backupFor(device.deviceId)
              return (
                <li key={device.deviceId} className={device.active ? '' : 'released'}>
                  <div>
                    <div>
                      {device.active ? (
                        <Pill tone="ok">In use</Pill>
                      ) : (
                        <Pill tone="muted">Released</Pill>
                      )}{' '}
                      <span className="devices__meta">set up {date(device.enrolledAt)}</span>
                    </div>
                    <div className="devices__meta">
                      Last checked in: {dateTime(device.lastLicenceCheckAt)}
                      {device.lastSeenVersion ? ` · version ${device.lastSeenVersion}` : ''}
                    </div>
                    {state ? <BackupDetail backup={state} /> : null}
                  </div>
                  {device.active ? (
                    <button
                      type="button"
                      className="danger small"
                      onClick={() => setConfirming(device)}
                    >
                      Release
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {confirming ? (
        <Confirm
          title="Release this device?"
          confirmLabel="Release it"
          busy={busy}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void release(confirming.deviceId)}
        >
          <p>
            This frees your device slot so you can set up a replacement machine straight away.
          </p>
          <p>
            <strong>The till on that PC stops selling</strong> at its next check — within a few
            hours, or immediately if it is online now.
          </p>
          <p>
            <strong>It does not erase anything on that machine.</strong> We cannot reach a PC
            that has been stolen. Its sales stay on its own disk, and if it ever connects again
            it can still send them here — which is how you get back the last day of trading from
            a machine you have lost.
          </p>
          <p>
            Everything already backed up to this account stays, and a replacement device
            restores from it.
          </p>
          {backupFor(confirming.deviceId)?.health === 'overdue' ||
          backupFor(confirming.deviceId)?.health === 'never' ? (
            <Alert tone="warn">
              This device has not backed up recently, so some of its trading may only exist on
              that machine. Release it anyway if the PC is gone — but if you still have it and it
              can get online, letting it back up first will save those sales.
            </Alert>
          ) : null}
        </Confirm>
      ) : null}
    </>
  )
}
